import { Link } from '@mui/material';
import {
  bindPopover,
  bindToggle,
  usePopupState,
} from 'material-ui-popup-state/hooks';

import {
  Box,
  Divider,
  DrawerProvider,
  Icons,
  List,
  ListItem,
  ListItemText,
  Stack,
  tokens,
  Tooltip,
} from '@cast/design-system';
import { getDocsLink, isChatLoaded, openChat } from '@cast/utils';

import { getUser } from '@cast/infrastructure-auth';
import {
  useIsDarkMode,
  useToggleDarkMode,
} from '@cast/shared-theme-data-access';
import { LogoFullSvg } from 'assets/svg';
import { Link as RouterLink } from 'components/router';
import { TrialBadge } from 'components/trial/TrialBadge';
import {
  doesReportingMonetizationApply,
  ReportingTrialStatusBadge,
  shouldShowReportingTrialStatus,
  useReportingTrial,
} from '@cast/shared-trial-ui';
import { ACTIVITY_PATHS, PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { AbilityGuard, useAbility } from '@cast/shared-ability-data-access';
import { NotificationsBell } from 'features/notifications';
import { useIsOrganizationAuditLogVisible } from 'hooks/permissions/useIsOrganizationAuditLogVisible';
import { useCreateOrganizationDialog } from 'features/organization';
import {
  AskCopilotButton,
  useChatbotState,
  useIsOpsPilotEnabled,
} from '@cast/features-organization-shared-opspilot';
import { useReportingTrialFleetStats } from '@cast/features-cluster-shared';
import { useNavigate } from '@cast/infrastructure-organization';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { useReportingContactSalesDialog } from 'core/integrations';
import {
  useReopenOnboardingWizard,
  useShouldShowGettingStartedHelpEntry,
} from 'features/onboarding-wizard';
import { useNavigateToReportingPlans } from 'features/organization/management/platform-usage';
import { HeaderPopover } from 'main-layout/header/_components';
import { HeaderIconButton } from 'main-layout/header/HeaderIconButton';
import { OrganizationMenu } from 'main-layout/header/OrganizationMenu';
import { ProfileMenu } from 'main-layout/header/ProfileMenu';
import { useMainLayout } from 'main-layout-op/_providers/MainLayoutProvider';
import { ConsoleRedesignAnnouncement } from 'main-layout-op/_components/ConsoleRedesignAnnouncement';

import { EXTERNAL_LINKS } from '@cast/constants';

const HeaderReportingTrialBadge = ({
  onViewPlans,
  onContactSales,
}: {
  onViewPlans: () => void;
  onContactSales: () => void;
}) => {
  const fleet = useReportingTrialFleetStats();
  return (
    <ReportingTrialStatusBadge
      fleet={fleet}
      onViewPlans={onViewPlans}
      onContactSales={onContactSales}
    />
  );
};

const HeaderTrialStatus = () => {
  const navigateToReportingPlans = useNavigateToReportingPlans();
  const { openReportingContactSales } = useReportingContactSalesDialog();
  const trial = useReportingTrial();

  if (doesReportingMonetizationApply(trial)) {
    if (trial.state === 'pre-trial' || trial.state === 'paid') {
      return null;
    }
    if (!shouldShowReportingTrialStatus(trial)) {
      return null;
    }
    return (
      <HeaderReportingTrialBadge
        onViewPlans={navigateToReportingPlans}
        onContactSales={() => openReportingContactSales()}
      />
    );
  }

  return <TrialBadge />;
};

const HelpMenuButton = () => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'help-menu',
  });
  const popoverState = bindPopover(popupState);
  const isPylonLoaded = isChatLoaded();
  const showGettingStarted = useShouldShowGettingStartedHelpEntry();
  const reopenGettingStarted = useReopenOnboardingWizard();

  return (
    <>
      <HeaderIconButton
        {...bindToggle(popupState)}
        aria-label="Help & Resources"
        data-testid="help-menu-btn"
      >
        <Icons.Question size={16} weight="fill" />
      </HeaderIconButton>

      <HeaderPopover {...popoverState} testId="help-menu">
        <List size="medium" sx={{ minWidth: '190px', py: 4 }}>
          {showGettingStarted ? (
            <ListItem
              testId="help-getting-started"
              onClick={() => {
                reopenGettingStarted();
                popoverState.onClose();
              }}
            >
              <ListItemText>Getting started</ListItemText>
            </ListItem>
          ) : null}

          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component="a"
            target="_blank"
            rel="noreferrer noopener"
            href={getDocsLink('docs')}
          >
            <ListItemText>
              <Stack direction="row" alignItems="center" gap={4}>
                Documentation
                <Icons.ArrowSquareOut size="1.3em" />
              </Stack>
            </ListItemText>
          </ListItem>

          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component="a"
            target="_blank"
            rel="noreferrer noopener"
            href="/api/v1/auth/academy/sso"
          >
            <ListItemText>
              <Stack direction="row" alignItems="center" gap={4}>
                Academy
                <Icons.ArrowSquareOut size="1.3em" />
              </Stack>
            </ListItemText>
          </ListItem>

          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component="a"
            target="_blank"
            rel="noreferrer noopener"
            href={EXTERNAL_LINKS.castai_slack_community}
          >
            <ListItemText>
              <Stack direction="row" alignItems="center" gap={4}>
                Community
                <Icons.ArrowSquareOut size="1.3em" />
              </Stack>
            </ListItemText>
          </ListItem>

          <ListItem
            onClick={() => {
              openChat();
              popoverState.onClose();
            }}
          >
            <Tooltip
              title={
                "Check your cookie settings if you don't see the chat bubble."
              }
              placement="right"
              wrapIf={!isPylonLoaded}
            >
              <ListItemText>Get support</ListItemText>
            </Tooltip>
          </ListItem>
        </List>
      </HeaderPopover>
    </>
  );
};

const Menu = () => {
  const { openModal: openCreateOrganizationModal } =
    useCreateOrganizationDialog();
  const { organizations, currentOrganization, setCurrentOrganization } =
    useOrganizations();
  const profile = getUser();
  const navigate = useNavigate();
  const { handleProductSelect } = useMainLayout();

  const { opsPilotStatus, onTryOpsPilot, tryOpsPilotDisabledTooltip } =
    useIsOpsPilotEnabled();

  const canViewThemeSwitch = useAbility().can('view', 'FeatureThemeSwitch');
  const isDarkMode = useIsDarkMode();
  const handleToggleDarkMode = useToggleDarkMode();

  const { open } = useChatbotState();
  const handleOpenChatbot = () => open(currentOrganization?.id ?? 'default');

  const handleTryOpsPilot = () => {
    handleProductSelect('settings');
    onTryOpsPilot();
  };

  const isOrgAuditLogEnabled = useIsOrganizationAuditLogVisible();

  const handleSettingsClick = () => {
    handleProductSelect('settings');
    navigate(PRODUCT_BASE_PATHS.settings);
  };

  const handleActivityLogClick = () => {
    handleProductSelect('auditLog');
    navigate(ACTIVITY_PATHS.activityEvents);
  };

  return (
    <Stack direction="row" justifyContent="flex-end" gap={8}>
      {opsPilotStatus === 'on' && (
        <>
          <AskCopilotButton onClick={handleOpenChatbot} />
          <Divider orientation="vertical" flexItem />
        </>
      )}

      {opsPilotStatus === 'off' && (
        <>
          <AskCopilotButton
            promptToEnable
            onClick={handleTryOpsPilot}
            disabled={!!tryOpsPilotDisabledTooltip}
            tooltip={tryOpsPilotDisabledTooltip}
          />
          <Divider orientation="vertical" flexItem />
        </>
      )}

      <ProfileMenu
        user={profile?.firstName || ''}
        email={profile?.email || ''}
      />

      <Divider orientation="vertical" flexItem />

      <Stack
        direction="row"
        gap="12px"
        ml="4px"
        alignItems="center"
        height="100%"
      >
        {isOrgAuditLogEnabled && (
          <AbilityGuard subject="OrganizationAuditLog" action="view">
            <HeaderIconButton
              onClick={handleActivityLogClick}
              aria-label="Activity log"
              data-testid="activity-log-header-btn"
            >
              <Icons.Table size={16} weight="fill" />
            </HeaderIconButton>
          </AbilityGuard>
        )}

        <HelpMenuButton />

        {canViewThemeSwitch && (
          <HeaderIconButton
            onClick={handleToggleDarkMode}
            aria-label="Toggle dark mode"
            data-testid="theme-switch-button"
          >
            {isDarkMode ? (
              <Icons.Sun size={16} weight="fill" />
            ) : (
              <Icons.Moon size={16} weight="fill" />
            )}
          </HeaderIconButton>
        )}

        <HeaderIconButton
          onClick={handleSettingsClick}
          aria-label="Organization settings"
          data-testid="organization-settings-button"
        >
          <Icons.GearSix size={16} weight="fill" />
        </HeaderIconButton>

        <AbilityGuard subject="OrganizationNotifications" action="view">
          <DrawerProvider>
            <NotificationsBell />
          </DrawerProvider>
        </AbilityGuard>

        <OrganizationMenu
          activeOrganizationId={currentOrganization?.id}
          organizations={organizations}
          onChangeOrganization={(org) => setCurrentOrganization(org.id)}
          onCreateNewOrganization={openCreateOrganizationModal}
        />
      </Stack>
    </Stack>
  );
};

type Props = {
  noSidebar: boolean | undefined;
  noMenu: boolean | undefined;
};

export const Header = (props: Props) => {
  const { isProductPickerOpen, openProductPicker } = useMainLayout();

  const handleHamburger = () => openProductPicker();

  return (
    <Box
      gridColumn="1 / -1"
      gridRow="toolbar"
      position="sticky"
      top="var(--escalation-header-height, 0px)"
      sx={{
        zIndex: 3,
        bgcolor: tokens.theme.bg.structural.page,
      }}
      data-testid="main-layout-header"
    >
      <ConsoleRedesignAnnouncement />

      <Stack
        direction="row"
        component="header"
        borderBottom="1px solid"
        borderColor={tokens.theme.border.structural.neutral}
        py={12}
        pl={4}
        pr={24}
        gap={8}
      >
        <Stack direction="row" gap={24} alignItems="center">
          <Stack direction="row" gap={8} alignItems="center">
            {!props.noSidebar && (
              <HeaderIconButton
                onClick={handleHamburger}
                aria-label="Toggle menu"
                active={isProductPickerOpen}
              >
                <Icons.List size={20} />
              </HeaderIconButton>
            )}
            <Link
              sx={{ display: 'flex', alignItems: 'center' }}
              component={RouterLink}
              to="/dashboard"
              data-testid="app-logo"
            >
              <LogoFullSvg
                aria-label="logo"
                color={tokens.theme.fg.structural.primary}
              />
            </Link>
          </Stack>
          <HeaderTrialStatus />
        </Stack>

        {!props.noMenu && (
          <Stack
            flexGrow={1}
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={8}
          >
            <Menu />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
